import { AddMaterial } from "./AddMaterial.jsx";
import { CFG, DECKS_KEY, LS, SUBJS_KEY, SUBJ_COLORS, _auth, _db, cloudSyncOnLogin, defaultSubjects, dk, fetchAcademyCode, fmtClock, nickKey, setSyncListener, setUid, tr } from "../core/platform.js";
import { COMPANY_MODE, MODELS, setAcademyMode, uid } from "../core/ai.js";
import { claimSession, logSessionStart, startUsageSession } from "../core/usageEvents.js";
import { Exam } from "./Exam.jsx";
import { Home } from "./Home.jsx";
import { Insight } from "./Insight.jsx";
import { Onboard, Settings } from "./Settings.jsx";
import { Prof } from "../ui/common.jsx";
import { Study } from "./Study.jsx";
import { Tutor } from "./Tutor.jsx";
import { UnitStudy } from "./UnitStudy.jsx";
import { WeakNotes } from "./WeakNotes.jsx";
import React from "react";
const { useState, useEffect, useRef, useCallback } = React;

// edition: "general"(범용 — 무엇이든 넣어 공부) | "student"(한국 중·고등 수학) | "us"(미국 수학) — 둘 다 단원별 공부 포함
function App({edition="general"}){
  const isStudent=edition==="student"||edition==="us";
  // 미국판(#us)은 기본 언어를 영어로 — tr(ko,en) 전역 스위치라 이 한 줄로 화면 문구 전체가 전환됨
  // 한국 중·고등(#student)은 언어를 잠근다 — 영어 자료를 넣어도 묻지 않고 한국어(대표 지시 2026-07-27)
  useEffect(()=>{
    if(edition==="student"){CFG.lang="ko";CFG.langLocked=true;LS.set("ng:lang","ko");return;}
    CFG.langLocked=false;
    if(edition==="us"&&CFG.lang!=="en"){CFG.lang="en";LS.set("ng:lang","en");}
  },[edition]);
  const [view,setView]=useState((CFG.key||COMPANY_MODE)?"home":"onboard");
  // 로그인 직후 학원코드를 받아오는 동안 true. 첫 로그인은 자리를 잡느라 왕복이 더 붙는데,
  // 그동안 키 입력 화면을 보여주면 "키를 넣으라는 건가" 싶다가 홈으로 튕기는 것처럼 보인다.
  const [linking,setLinking]=useState(false);
  const [decks,setDecks]=useState([]);
  const [subjects,setSubjects]=useState(defaultSubjects());
  const [filterSubj,setFilterSubj]=useState("all");
  const [activeDeck,setActiveDeck]=useState(null);
  const [examTopic,setExamTopic]=useState(null);   // 책 시험: 선택 단원 src+문항수 (topic 기반)
  const [noteDeck,setNoteDeck]=useState(null);
  const [homeNotice,setHomeNotice]=useState(false);  // 단원 생성 직후 홈에 "풀어보라" 안내 배너
  // 자료/단원으로 개념을 만드는 중인지 — 헤더로 나가면 만들던 게 통째로 날아가서, 나가기 전에 묻는다
  const [genBusy,setGenBusy]=useState(false);
  const [user,setUser]=useState(null);
  const [nick,setNick]=useState("");
  const [showProfile,setShowProfile]=useState(false);
  const [syncBusy,setSyncBusy]=useState(false);
  const [cloudStatus,setCloudStatus]=useState("off");  // off | ok | error
  const [lastSyncAt,setLastSyncAt]=useState(0);
  // 환영 화면: 앱 들어올 때 닉네임 부르며 잠깐 인사 (기존 사용자만 — 온보딩 중엔 X)
  const [welcomed,setWelcomed]=useState(!CFG.key);
  const [wHide,setWHide]=useState(false);
  const userRef=useRef(null);
  const dismissWelcome=()=>{setWHide(true);setTimeout(()=>setWelcomed(true),360);};
  useEffect(()=>{if(!CFG.key)return;const t=setTimeout(dismissWelcome,2800);return ()=>clearTimeout(t);},[]);

  // 클라우드 동기화 상태 구독 (초기 병합 + 이후 변경 저장 모두 반영)
  useEffect(()=>{
    setSyncListener(s=>{
      if(s.busy!==undefined)setSyncBusy(s.busy);
      if(s.ok!==undefined)setCloudStatus(s.ok?"ok":"error");
      if(s.at)setLastSyncAt(s.at);
    });
    return ()=>setSyncListener(null);
  },[]);

  // 생성 중 헤더 이탈 가드 — 나가겠다고 하면 만들던 판은 버려진 것이므로 표시도 같이 내린다
  function leaveGen(){
    if(!genBusy)return true;
    if(!confirm(tr("아직 문제를 만드는 중이야. 지금 나가면 만들던 게 사라져.\n그래도 나갈까?",
                   "Still building your questions. Leaving now discards them.\nLeave anyway?")))return false;
    setGenBusy(false);return true;
  }
  const refresh=()=>setDecks(LS.get(DECKS_KEY)||[]);
  useEffect(()=>{refresh();},[]);

  // 사용량 세션 = "공부 시작"으로 학습 화면에 들어온 한 판. 진입할 때마다 새 세션 id.
  useEffect(()=>{
    if(!["study","exam","tutor"].includes(view))return;
    // 방금 올린 자료로 바로 들어온 경우엔 업로드 때 연 세션을 이어간다(세션 표식도 그때 이미 남겼다).
    if(claimSession(activeDeck&&activeDeck.id))return;
    startUsageSession();
    const modeLabel=view==="tutor"?"개념강의":view==="exam"?"시험"
      :(activeDeck&&activeDeck.studyType==="quiz"?"플래시":"과정");
    logSessionStart(activeDeck&&Array.isArray(activeDeck.concepts)?activeDeck.concepts.length:null,
      activeDeck&&activeDeck.name?activeDeck.name+" · "+modeLabel:modeLabel,modeLabel);
  },[view]);

  // 구글 로그인 상태 구독 + 클라우드 동기화
  useEffect(()=>{
    if(!_auth)return;
    return _auth.onAuthStateChanged(async u=>{
      // #beta 신청은 익명 로그인으로 자리만 잠근다 — 이메일이 없으므로 학생 로그인으로 치면 안 된다.
      if(u&&u.isAnonymous)u=null;
      setUser(u);userRef.current=u;
      if(!u){
        setUid(null);setNick("");
        // ADR-014: 로그아웃 → 로그인으로 켠 학원 모드 해제(해시로 켰으면 유지). 키도 없으면 온보딩으로.
        setAcademyMode("");
        if(!CFG.key&&!COMPANY_MODE)setView("onboard");
        return;
      }
      setUid(u.uid);
      // ADR-014: 명부(codes/{이메일})에서 학생코드 조회 → 있으면 학원 모드(회사키 프록시 경유).
      // 없거나 조회 실패면 그대로 개인 모드. 키 입력 화면에 있었다면 홈으로 진입시킨다.
      setLinking(true);
      fetchAcademyCode(u.email,u.uid,u.displayName).then(code=>{
        if(!code)return;
        setAcademyMode(code);
        setView(v=>v==="onboard"?"home":v);
      }).finally(()=>setLinking(false));
      if(_db){
        try{await cloudSyncOnLogin(u.uid);}catch(e){console.warn("[sync]",e);}
        refresh();setSubjects(defaultSubjects());
      }
      const stored=LS.get(nickKey(u.uid));
      const n=stored||u.displayName||(u.email?u.email.split("@")[0]:tr("학습자","Learner"));
      if(!stored)LS.set(nickKey(u.uid),n);
      setNick(n);
    });
  },[]);

  async function login(){
    if(!_auth){alert(tr("로그인 기능을 쓸 수 없어. 인터넷 연결이나 설정을 확인해줘.","Sign-in isn't available. Check your connection or settings."));return;}
    try{
      await _auth.signInWithPopup(new window.firebase.auth.GoogleAuthProvider());
    }catch(e){
      if(e.code==="auth/popup-closed-by-user"||e.code==="auth/cancelled-popup-request")return;
      alert(tr("로그인 실패: ","Sign-in failed: ")+(e.message||e.code));
    }
  }
  async function logout(){try{await _auth.signOut();}catch(e){}setShowProfile(false);}
  function saveNick(n){const v=(n||"").trim();if(!v||!user)return;setNick(v);LS.set(nickKey(user.uid),v);}

  function openStudy(id,mode){const d=LS.get(dk(id));if(!d)return;if(mode==="exam"){setExamTopic(null);setActiveDeck(d);setView("exam");return;}if(mode==="learn"){setActiveDeck(d);setView("tutor");return;}setActiveDeck({...d,studyType:mode||d.studyType||"explain"});setView("study");}
  // 책 소주제 하나만 집중 학습(이해=explain / 암기=quiz). focusId로 그 개념만 출제.
  function openConcept(deckId,conceptId,mode){const d=LS.get(dk(deckId));if(!d)return;setActiveDeck({...d,studyType:mode||"explain",focusId:conceptId});setView("study");}
  function openNotes(id){const d=LS.get(dk(id));if(d){setNoteDeck(d);setView("notes");}}
  function studyWeak(deck){
    const fresh=LS.get(dk(deck.id))||deck;
    const weakConcepts=(fresh.concepts||[]).filter(c=>(c.box||1)<=3||c.lapses>0);
    if(!weakConcepts.length){alert(tr("아직 약점 개념이 없어! 전체 공부부터 해봐 💪","No weak concepts yet! Study the full set first 💪"));return;}
    setActiveDeck({...fresh,concepts:weakConcepts});
    setView("study");
  }

  function saveSubjects(s){setSubjects(s);LS.set(SUBJS_KEY,s);}

  const filteredDecks=filterSubj==="all"?decks:decks.filter(d=>d.subjId===filterSubj);

  return(
    <>
      {!welcomed&&(
        <div onClick={dismissWelcome} style={{position:"fixed",inset:0,zIndex:200,cursor:"pointer",
          background:"linear-gradient(160deg,var(--pri) 0%,var(--pri-lt) 60%,var(--pri-lt2) 100%)",
          display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:13,color:"#fff",
          opacity:wHide?0:1,transition:"opacity .36s ease",textAlign:"center",padding:24}}>
          <div style={{background:"#fff",borderRadius:"50%",padding:16,boxShadow:"0 14px 44px rgba(0,0,0,.22)"}}><Prof size={86}/></div>
          <div style={{fontFamily:"'Jua',sans-serif",fontSize:27,lineHeight:1.25,marginTop:4}}>
            {nick?tr("안녕, ","Hi, ")+nick+"! 👋":tr("어서 와! 👋","Welcome! 👋")}
          </div>
          <div style={{fontSize:15,opacity:.95}}>{tr("오늘도 프로비랑 같이 공부하자 📚","Learn smarter. I'll guide the way. — let's study 📚")}</div>
          <div style={{fontSize:12.5,opacity:.72,marginTop:20}}>{tr("화면을 누르면 시작","Tap anywhere to start")}</div>
        </div>
      )}
      <div className="hd">
        <div className="brand" onClick={()=>{if(leaveGen())setView((CFG.key||COMPANY_MODE)?"home":"onboard");}}>
          <Prof size={44}/><div><h1>Provee<span style={{color:"var(--pri)"}}>.</span></h1><div className="tag">{isStudent?tr("중·고등 수학 — 네가 설명해봐, 내가 채점할게","Middle·High math — you explain, I'll grade"):tr("네가 설명해봐, 내가 채점할게","You explain, I'll grade")}</div></div>
        </div>
        <div className="hd-r">
          {view!=="home"&&CFG.key&&<button className="btn gho sm" onClick={()=>{if(!leaveGen())return;refresh();setView("home");}}>{tr("← 목록","← Back")}</button>}
          {_auth&&(user
            ?<button className="btn gho sm" onClick={()=>setShowProfile(true)} title={syncBusy?tr("동기화 중…","Syncing…"):cloudStatus==="ok"?tr("동기화 완료","Synced"):tr("내 프로필","My profile")}>
                {user.photoURL&&<img src={user.photoURL} alt="" style={{width:18,height:18,borderRadius:"50%",verticalAlign:"middle",marginRight:6}}/>}
                {nick||tr("내 프로필","My profile")}
                <span style={{marginLeft:5}}>{syncBusy?"☁️…":cloudStatus==="ok"?"☁️✓":cloudStatus==="error"?"⚠️":""}</span>
              </button>
            :<button className="btn gho sm" onClick={login}>{tr("🔑 로그인","🔑 Sign in")}</button>
          )}
          <button className="btn gho sm" onClick={()=>{if(leaveGen())setView("settings");}}>{tr("⚙️ 설정","⚙️ Settings")}</button>
        </div>
      </div>

      {showProfile&&user&&(
        <div onClick={()=>setShowProfile(false)} style={{position:"fixed",inset:0,background:"rgba(34,28,57,.42)",zIndex:60,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div onClick={e=>e.stopPropagation()} className="card" style={{maxWidth:340,width:"100%",padding:22,display:"flex",flexDirection:"column",gap:14}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              {user.photoURL&&<img src={user.photoURL} alt="" style={{width:46,height:46,borderRadius:"50%"}}/>}
              <div style={{minWidth:0}}>
                <div style={{fontFamily:"'Jua',sans-serif",fontSize:16,color:"var(--ink)"}}>{nick}</div>
                <div style={{fontSize:12,color:"var(--sub)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{user.email}</div>
              </div>
            </div>
            <div style={{fontSize:12.5,padding:"8px 12px",borderRadius:10,
              background:(!syncBusy&&cloudStatus==="ok")?"var(--ok-bg)":syncBusy?"var(--pri-bg)":"var(--warn-bg)",
              border:"1px solid "+((!syncBusy&&cloudStatus==="ok")?"var(--ok-bd)":syncBusy?"var(--pri-bd)":"var(--warn-bd)"),
              color:(!syncBusy&&cloudStatus==="ok")?"var(--ok-ink)":syncBusy?"var(--pri-ink)":"var(--warn-ink2)",lineHeight:1.6}}>
              {syncBusy?tr("☁️ 동기화 중…","☁️ Syncing…")
               :cloudStatus==="ok"?(tr("✅ 동기화 완료 — 어느 기기서든 이어집니다.","✅ Synced — continues on any device.")+(lastSyncAt?tr(" (마지막 "," (last ")+fmtClock(lastSyncAt)+")":""))
               :cloudStatus==="error"?tr("⚠️ 클라우드 연결 실패 — Firestore 설정을 확인해줘 (지금은 이 기기에만 저장).","⚠️ Cloud connection failed — check Firestore setup (saved on this device for now).")
               :tr("⚠️ 클라우드 미설정 — 이 기기에만 저장됩니다.","⚠️ Cloud not set up — saved on this device only.")}
            </div>
            <div className="selectrow">
              <label>{tr("닉네임","Nickname")}</label>
              <input className="field" value={nick} maxLength={20} onChange={e=>{const v=e.target.value;setNick(v);if(v.trim()&&user)LS.set(nickKey(user.uid),v.trim());}} onBlur={e=>saveNick(e.target.value)}/>
            </div>
            <div className="row">
              <button className="btn pri" onClick={()=>{saveNick(nick);setShowProfile(false);}}>{tr("저장","Save")}</button>
              <button className="btn gho" onClick={logout}>{tr("로그아웃","Sign out")}</button>
            </div>
          </div>
        </div>
      )}

      {view==="onboard"&&(linking
        ?<div className="card" style={{padding:28,textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",gap:14}}>
           <div className="spinner"/>
           <div style={{fontSize:15,lineHeight:1.8}}>계정을 연동하는 중이에요</div>
           <div className="hint">잠시만 기다려주세요.</div>
         </div>
        :<Onboard onDone={()=>setView("home")} onLogin={login} signedIn={!!user}/>)}
      {view==="settings"&&<Settings onDone={()=>setView((CFG.key||COMPANY_MODE)?"home":"onboard")}/>}
      {view==="home"&&(
        <>
          <SubjectTabs subjects={subjects} active={filterSubj} onChange={setFilterSubj} onSave={saveSubjects}/>
          <Home decks={filteredDecks} subjects={subjects} onAdd={()=>setView("add")} onUnits={isStudent?()=>setView("units"):null} onOpen={(id,mode)=>{setHomeNotice(false);openStudy(id,mode);}} onNotes={openNotes} onChanged={refresh} nick={nick} onInsight={()=>setView("insight")} notice={homeNotice} onNoticeClose={()=>setHomeNotice(false)}/>
        </>
      )}
      {view==="insight"&&<Insight onExit={()=>{refresh();setView("home");}}/>}
      {view==="add"&&<AddMaterial subjects={subjects} onSave={saveSubjects} onBusy={setGenBusy} onDone={(deckName)=>{setGenBusy(false);refresh();setHomeNotice(deckName||true);setView("home");}} onCancel={()=>setView("home")}/>}
      {view==="units"&&<UnitStudy edition={edition} subjects={subjects} onSave={saveSubjects} onBusy={setGenBusy} onDone={(deckId,deckName)=>{setGenBusy(false);refresh();if(deckId)setHomeNotice(deckName||true);setView("home");}} onCancel={()=>setView("home")}/>}
      {view==="study"&&activeDeck&&<Study deck={activeDeck} subjects={subjects} onExit={()=>{refresh();setView("home");}}/>}
      {view==="exam"&&(activeDeck||examTopic)&&<Exam deck={examTopic?null:activeDeck} topic={examTopic} onExit={()=>{setExamTopic(null);refresh();setView("home");}}/>}
      {view==="tutor"&&activeDeck&&<Tutor deck={activeDeck} onExit={()=>{refresh();setView("home");}} onPractice={(cid,mode)=>openConcept(activeDeck.id,cid,mode)} onExam={(topic)=>{setExamTopic(topic);setView("exam");}}/>}
      {view==="notes"&&noteDeck&&<WeakNotes deck={noteDeck} onBack={()=>{refresh();setView("home");}} onStudy={studyWeak}/>}

      <div className="footer">{tr("키와 자료는 이 기기에만 저장","Keys & data stay on this device only")} · {MODELS.find(m=>m.id===CFG.model)?.label.split(" ·")[0]||"Claude"}</div>
    </>
  );
}

/* ── 과목 탭 바 ── */
function SubjectTabs({subjects,active,onChange,onSave}){
  const [adding,setAdding]=useState(false);
  const [newName,setNewName]=useState("");
  const [editId,setEditId]=useState(null);
  const [editName,setEditName]=useState("");

  function addSubject(){
    const name=newName.trim();if(!name)return;
    const color=SUBJ_COLORS[subjects.length%SUBJ_COLORS.length];
    const s=[...subjects,{id:uid(),name,color}];
    onSave(s);setNewName("");setAdding(false);
  }
  function startEdit(s,e){
    e.stopPropagation();setEditId(s.id);setEditName(s.name);
  }
  function saveEdit(){
    const n=editName.trim();if(!n)return;
    onSave(subjects.map(s=>s.id===editId?{...s,name:n}:s));setEditId(null);
  }
  function delSubject(s,e){
    e.stopPropagation();
    if(!confirm(tr('"'+s.name+'" 과목 삭제할까? 덱 자료는 남아있어.','Delete folder "'+s.name+'"? Your decks stay.')))return;
    onSave(subjects.filter(x=>x.id!==s.id));
    if(active===s.id)onChange("all");
  }
  const btnStyle={background:"none",border:"none",cursor:"pointer",padding:"0 2px",opacity:.55,color:"inherit",fontSize:12,lineHeight:1};

  return(
    <div style={{marginBottom:20}}>
      <div className="sub-tabs">
        <button className={"sub-tab"+(active==="all"?" on":"")} onClick={()=>onChange("all")}>{tr("전체","All")}</button>
        {subjects.map(s=>
          editId===s.id?(
            <div key={s.id} className="subj-new" style={{display:"inline-flex",margin:0}}>
              <input value={editName} onChange={e=>setEditName(e.target.value)}
                onKeyDown={e=>{if(e.key==="Enter")saveEdit();if(e.key==="Escape")setEditId(null);}}
                autoFocus style={{width:100}}/>
              <button className="btn pri xs" onClick={saveEdit}>{tr("저장","Save")}</button>
              <button className="btn gho xs" onClick={()=>setEditId(null)}>{tr("취소","Cancel")}</button>
            </div>
          ):(
            <div key={s.id} className={"sub-tab"+(active===s.id?" on":"")}
              style={{display:"inline-flex",alignItems:"center",gap:3,paddingRight:6,cursor:"pointer"}}
              onClick={()=>onChange(s.id)}>
              <span className="sub-dot" style={{background:s.color}}/>
              <span>{s.name}</span>
              <button style={btnStyle} onClick={e=>startEdit(s,e)} title={tr("수정","Edit")}>✎</button>
              <button style={{...btnStyle,fontSize:14}} onClick={e=>delSubject(s,e)} title={tr("삭제","Delete")}>×</button>
            </div>
          )
        )}
        <button className="btn gho xs" onClick={()=>setAdding(v=>!v)}>{tr("+ 과목","+ Folder")}</button>
      </div>
      {adding&&(
        <div className="subj-new">
          <input placeholder={tr("과목 이름 (예: 통계학)","Folder name (e.g. Statistics)")} value={newName}
            onChange={e=>setNewName(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter")addSubject();if(e.key==="Escape")setAdding(false);}}
            autoFocus/>
          <button className="btn pri xs" onClick={addSubject}>{tr("추가","Add")}</button>
          <button className="btn gho xs" onClick={()=>setAdding(false)}>{tr("취소","Cancel")}</button>
        </div>
      )}
    </div>
  );
}

/* ── 온보딩 ── */

export { App, SubjectTabs };
